import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Form, reduxForm } from 'redux-form'
import classnames from 'classnames/bind'
import SortDropdown from '../../sortDropdown'
import {
  ConditionFiltersContent,
  ConditionFiltersHeader,
} from '../conditionFilters'
import { MoreFiltersContent } from '../moreFilters'
import { FeaturedFiltersContent } from '../featuredFilters'
import { PropertyFiltersContent } from '../propertyFilters'
import { AuctionTypeFiltersContent } from '../auctionTypeFilters'
import { AssetTypeFiltersContent } from '../assetTypeFilters'
import {
  financingAvailableSelected,
  interiorAccessSelected,
  hideEndingSoonSelected,
  vacantOccupancyStatus,
  allListingTypesSelected,
  activeListingTypeSelected,
  brokerCoopAvailableSelected,
  noBuyerPremiumFilterSelected,
  showReserveSelected,
  maoFilterSelected,
  AUCTION_TYPE,
} from '../../../constants/filters'
import styles from './styles.scss'
const cx = classnames.bind(styles)

export const BASE_ELM_ID = 'all_filters'
export const FORM_NAME = 'allFilters'

class AllFiltersForm extends Component {
  static propTypes = {
    sort: PropTypes.object.isRequired,
    featureFlags: PropTypes.object.isRequired,
    updateFilters: PropTypes.func.isRequired,
    updateSorting: PropTypes.func.isRequired,
    submitForms: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    marketingTagsConfig: PropTypes.array.isRequired,
  }

  submitForms = () => {
    this.props.submitForms(FORM_NAME)
  }

  updateFilters = filters => {
    const newFilters = {
      offset: 0,
      // PropertyFilters
      beds:
        filters.beds && filters.beds !== 'any' ? parseInt(filters.beds) : null,
      baths:
        filters.baths && filters.baths !== 'any'
          ? parseInt(filters.baths)
          : null,
      sqft: {
        min: filters.sqft_min ? parseInt(filters.sqft_min) : null,
        max: filters.sqft_max ? parseInt(filters.sqft_max) : null,
      },
      propertyType: [
        ...(filters.single_family ? ['single-family'] : []),
        ...(filters.multi_family ? ['multi-family'] : []),
        ...(filters.condos_townhouses ? ['condos-townhouses'] : []),
        ...(filters.land ? ['land'] : []),
      ],
      // AssetTypes
      assetType: [
        ...(filters.bankOwned ? ['bank-owned', 'newly-foreclosed'] : []),
        ...(filters.foreclosure ? ['foreclosures'] : []),
        ...(filters.shortSales ? ['short-sales'] : []),
        ...(filters.privateSeller ? ['private-seller'] : []),
      ],
      // AuctionTypeFilters
      auctionFormat: [
        ...(filters.online ? ['online'] : []),
        ...(filters.live ? ['in_person'] : []),
        ...(filters.remoteBid ? ['remote_bid'] : []),
        ...(filters.directOffer ? ['direct_offer'] : []),
        ...(filters.offer ? ['offer'] : []),
      ],
      // ConditionFilters
      accessAvailable: !!filters.access_available && interiorAccessSelected,
      financingAvailable:
        !!filters.financing_available && financingAvailableSelected,
      occupancyStatus: filters.occupancy_status ? vacantOccupancyStatus : [],
      inspectionAllowed: filters.inspectionAllowed ? 'y' : false,
      // MoreFilters
      hideEndingSoon: !!filters.hide_ending_soon && hideEndingSoonSelected,
      leasebackRequired: filters.leaseback_required ? 'n' : null,
      brokerCoopAvailable:
        !!filters.broker_coop_available && brokerCoopAvailableSelected,
      noBuyerPremiumFilter:
        !!filters.no_buyer_premium_filter && noBuyerPremiumFilterSelected,
      presale: !!filters.presale && maoFilterSelected,
      showReserve: !!filters.show_reserve && showReserveSelected,
      listingType: filters.listing_type
        ? allListingTypesSelected
        : activeListingTypeSelected,
      marketingTag: null,
    }

    const tagList = []
    for (const currentMarketingTag of this.props.marketingTagsConfig) {
      const tag = currentMarketingTag.tag
      if (
        Object.getOwnPropertyDescriptor(filters, tag) &&
        filters[tag] === tag
      ) {
        tagList.push(tag)
      }
      newFilters.marketingTag = tagList.join(',').toLowerCase()
    }
    this.props.updateFilters(newFilters)
  }

  render() {
    const {
      sort,
      featureFlags,
      handleSubmit,
      updateSorting,
      marketingTagsConfig,
      ...rest
    } = this.props
    const { type: sortType, order: sortOrder } = sort
    const shortSalesFilterEnabled =
      featureFlags['fe.resi.search.shortsales-enabled']

    const privateSellerFilterEnabled =
      featureFlags['fe.resi.search.private-seller-enabled']

    const inspectionAllowedFilterEnabled =
      featureFlags['fe.resi.search.enable-inspection-allowed']
    const leasebackRequiredFilterEnabled =
      featureFlags['fe.resi.search.enable-leaseback-required']
    const privateSellerEnabled = featureFlags['fe.resi.enable-private-seller']

    return (
      <Form
        className={cx('ui', 'form', 'extra-compact')}
        data-elm-id={`${BASE_ELM_ID}_form`}
        onSubmit={handleSubmit(this.updateFilters)}
      >
        <label className={cx('filter-section-label')} style={{ marginTop: 0 }}>
          Sort
        </label>

        <SortDropdown
          direction='right'
          onSelect={updateSorting}
          selectedDirection={sortOrder}
          selectedValue={sortType}
          featureFlags={featureFlags}
          elmId={BASE_ELM_ID}
          selection
        />
        <label className={cx('filter-section-label')}>Property</label>

        <PropertyFiltersContent
          {...rest}
          elmId={`${BASE_ELM_ID}_property_section`}
          submitForms={this.submitForms}
        />

        <label className={cx('filter-section-label')}>Asset Type</label>

        <AssetTypeFiltersContent
          {...rest}
          shortSalesFilterEnabled={shortSalesFilterEnabled}
          privateSellerFilterEnabled={privateSellerFilterEnabled}
          privateSellerEnabled={privateSellerEnabled}
          elmId={`${BASE_ELM_ID}_asset_type_section`}
          submitForms={this.submitForms}
        />

        <label className={cx('filter-section-label')}>{AUCTION_TYPE}</label>
        <AuctionTypeFiltersContent
          {...rest}
          elmId={`${BASE_ELM_ID}_auction_type_section`}
          submitForms={this.submitForms}
        />

        <label className={cx('filter-section-label')}>Conditions</label>
        {!privateSellerEnabled && <ConditionFiltersHeader />}
        <ConditionFiltersContent
          {...rest}
          privateSellerEnabled={privateSellerEnabled}
          inspectionAllowedFilterEnabled={inspectionAllowedFilterEnabled}
          elmId={`${BASE_ELM_ID}_condition_section`}
          submitForms={this.submitForms}
        />
        <label className={cx('filter-section-label')}>Featured</label>
        <FeaturedFiltersContent
          {...rest}
          elmId={`${BASE_ELM_ID}_featured_section`}
          submitForms={this.submitForms}
          marketingTagsConfig={marketingTagsConfig}
        />
        <label className={cx('filter-section-label')}>More Filters</label>
        <MoreFiltersContent
          {...rest}
          leasebackRequiredFilterEnabled={leasebackRequiredFilterEnabled}
          elmId={`${BASE_ELM_ID}_more_section`}
          submitForms={this.submitForms}
        />
      </Form>
    )
  }
}

export default reduxForm({
  form: FORM_NAME,
  enableReinitialize: true,
})(AllFiltersForm)
